import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { localised, useI18n, type CopyKey } from './i18n';
import { bandOf } from './Curriculum';
import {
  currentObjective,
  downloadSheet,
  getProgress,
  isError,
  type ProgressView,
  type SubjectProgress,
} from './api';
import { usePlans } from './plan/Plans';

/**
 * How far along you are, and which subjects are letting you down.
 *
 * The whole difficulty of this panel is honesty. Almost every number a progress
 * screen can show has a flattering reading and a true one, and the flattering
 * reading is usually the one that falls out of the arithmetic: an average over
 * items nobody has been asked about, a percentage with an invisible denominator, a
 * daily cap presented as a rate. So three rules run through everything below.
 *
 * **A ratio never appears without its denominator.** "You would get 100% of these
 * right" is true of the single question this learner has answered and useless as a
 * statement about a curriculum of four hundred and sixty-nine.
 *
 * **"Not asked yet" and "got it wrong" are never one bar.** They are the only two
 * things here a learner can respond to differently — one wants time, the other wants
 * attention — and merging them into "not yet mastered" throws away the whole point of
 * the screen.
 *
 * **A rounded zero says so.** A curriculum three thousandths mastered rounds to 0%,
 * which is fine; one that rounds to 100% while a question remains unanswered is the
 * lie this screen exists not to tell, so both ends are spelled out rather than
 * rounded through.
 *
 * It renders only under a running objective, because review state does not exist
 * before Start and a plan awaiting approval is not a plan at nought per cent.
 */

/**
 * How the subjects are ordered, and why the default is not the plan's own order.
 *
 * "Which of these am I worst at" is the question this list exists to answer, and
 * fourteen subjects in plan order make it a scan. Plan order is still one tap away,
 * because it is the order the learner arranged and the one the curriculum screen
 * below shows.
 */
type Ordering = 'weakest' | 'plan';

/**
 * A percentage that never rounds through either end.
 *
 * `Math.round` alone turns three thousandths into "0%" — acceptable — and would turn
 * 0.996 into "100%" on a curriculum with unanswered questions left in it, which is
 * the single most misleading thing this panel could print. Both ends therefore get a
 * word rather than a number.
 *
 * Exported since the chat's header grew a percentage of its own (F-112). One rounding rule
 * and not two: a header saying 100% above a panel saying >99% for the same objective is the
 * lie this function exists to prevent, told by the screen the learner is looking at most.
 */
export function percent(value: number): string {
  const rounded = Math.round(value * 100);
  if (rounded === 0 && value > 0) return '<1%';
  if (rounded === 100 && value < 1) return '>99%';
  return `${rounded}%`;
}

/**
 * The three-part bar, which is the same three counts as the line beneath it.
 *
 * Widths come from the counts directly rather than from any of the percentages, so
 * the picture and the numbers cannot drift apart — the headline figure is literally
 * the width of the first segment for a single subject.
 *
 * Hidden from a screen reader: it is a second, wordless telling of the sentence
 * beside it, and read out it becomes three unlabelled proportions.
 */
function Shape({ holding, struggling, untested }: {
  holding: number;
  struggling: number;
  untested: number;
}) {
  const total = holding + struggling + untested;
  if (total === 0) return null;
  const width = (part: number) => `${(part / total) * 100}%`;

  return (
    <span className="shape" aria-hidden="true">
      <span className="shape-holding" style={{ width: width(holding) }} />
      <span className="shape-struggling" style={{ width: width(struggling) }} />
      <span className="shape-untested" style={{ width: width(untested) }} />
    </span>
  );
}

function Subject({ subject, label }: { subject: SubjectProgress; label: string }) {
  const { t, locale } = useI18n();
  const { evidence } = subject;

  return (
    <li className={subject.active ? 'subject' : 'subject is-off'}>
      <p className="node-head">
        <span className="node-title">{localised(subject.title, locale, t('cur.untitled'))}</span>
        {!subject.active && <span className="pill pill-off">{t('cur.active.off')}</span>}
      </p>
      <p className="node-weight">
        {/*
         * The same word the plan screen puts on this subject, from the same function.
         * It is here because it is what the headline above is weighted by, and a
         * learner cannot judge a weighted number without seeing the weights — but the
         * band vocabulary does not carry over to mastery itself, which is a proportion
         * of questions and says so as one.
         */}
        <span className="weight-band">{t(bandOf(subject.weight))}</span>
        <span className="subject-score">
          {subject.mastery === null ? t('prog.subject.none') : percent(subject.mastery)}
        </span>
      </p>
      <Shape
        holding={evidence.holding}
        struggling={evidence.struggling}
        untested={evidence.untested}
      />
      {evidence.questions > 0 && <p className="muted small">{label}</p>}
    </li>
  );
}

/**
 * The screen: a tab per plan, and one plan's numbers underneath. F-151 step 8.
 *
 * **A wrapper rather than a branch inside the panel**, because the panel below returns from
 * four places — loading, unreadable, nothing running, and the real thing — and a bar rendered
 * inside it would have to be repeated in each of them or would vanish in three states out of
 * four. The learner would lose the way back to their other plan at exactly the moments the
 * screen is least useful.
 *
 * The plans are read here and nowhere else on this screen. It is one extra request, and it is
 * the request that pays for the tabs: `GET /api/progress` answers for one plan and has no
 * reason to know how many there are.
 */
export default function Progress() {
  const [search] = useSearchParams();
  /*
   * Which plan, from the address. `?o=` is step 6's parameter — what a tapped notification
   * carries and what a tab links to — so this screen learns about the plural without a second
   * spelling of "which plan".
   *
   * Absent means "the plan the API defaults to", which is what this screen has always asked
   * for and what every learner with one plan gets. Passed straight through rather than waiting
   * for the plans list: `GET /api/progress` resolves the default itself, so the numbers load in
   * one request exactly as they did before this step.
   */
  const objectiveId = search.get('o') ?? undefined;
  /*
   * **This read is for the header now, not for a bar on this screen** (2026-09-20).
   *
   * The pill bar that used to sit above this panel is gone; choosing a plan is a control in the
   * fixed header, above every screen rather than above three. The read stays because it is
   * this screen's own and costs the header nothing: `observe` hands it over, so a learner who
   * opens progress refreshes the selector's list and its counts without a second request.
   *
   * A failure hands nothing over rather than handing over an empty list, which is the same
   * distinction the bar made: absent renders no selector, empty would claim there are no plans.
   */
  const { observe } = usePlans();
  useEffect(() => {
    void currentObjective().then((result) => {
      if (isError(result)) return;
      observe({ plans: result.objectives, defaultId: result.objective?.objectiveId });
    });
  }, [observe]);

  return <ProgressPanel objectiveId={objectiveId} />;
}

function ProgressPanel({ objectiveId }: { objectiveId: string | undefined }) {
  const { t } = useI18n();
  /*
   * `undefined` until the read lands, `null` when nothing is running. The three
   * states are kept apart because they say different things: still loading, nothing
   * to report, and here is where you are. Collapsing the first two would flash
   * "nothing running" under an objective that is.
   */
  const [progress, setProgress] = useState<ProgressView | null | undefined>();
  const [unreadable, setUnreadable] = useState(false);
  const [ordering, setOrdering] = useState<Ordering>('weakest');
  /*
   * The review sheet's own in-flight and error state, and deliberately not shared
   * with `unreadable` above.
   *
   * A single error state serving two actions renders in both places at once: Leo
   * reported exactly that — "the error came up in multiple places" — and it has been
   * fixed twice already. `downloading` is separate for the same reason; the panel
   * must be able to say that the sheet is being written without implying the numbers
   * above it are still loading.
   */
  const [downloading, setDownloading] = useState(false);
  const [sheetError, setSheetError] = useState<CopyKey | null>(null);

  /*
   * Asked once, and deliberately not polled.
   *
   * Nothing here changes while the learner is looking at it: progress moves when an
   * answer is typed in the chat, which may be a different device and a different moment.
   * A fifteen-second poll would be a request a minute over mobile data for a screen
   * that would show the same numbers each time.
   */
  useEffect(() => {
    /*
     * Back to "loading" before the new plan's numbers land, and this is not a nicety.
     *
     * Leaving the previous plan's mastery on screen under the plan the tab bar has just
     * selected is the same fault `Chat.tsx` describes as "a header naming the previous plan
     * above this one's thread, and quieter" — a percentage is believed on sight, and nothing
     * about it says which plan it belongs to. Nought of a second of "loading" is the honest
     * alternative.
     */
    let current = true;
    setProgress(undefined);
    setUnreadable(false);
    // The plan the address names, so a tab shows that plan's numbers and not the default
    // plan's. `undefined` is the unqualified request this screen has always made.
    void getProgress(objectiveId).then((result) => {
      // The screen moved on while this was in the air. See above: a late answer for the
      // previous plan would overwrite the one the learner is looking at now.
      if (!current) return;
      if (isError(result)) {
        // Reported rather than shown as empty progress. "Nought per cent" and "we
        // could not read it" are opposite messages, and this panel is the one place
        // in the app where confusing them would be actively demoralising.
        setUnreadable(true);
        setProgress(null);
        return;
      }
      setProgress(result.progress);
    });
    return () => {
      current = false;
    };
    /*
     * The plan is a dependency because the numbers are about it: tapping another tab changes
     * the address without unmounting this component, and an effect that ran once would leave
     * the previous plan's mastery under the new plan's tab.
     */
  }, [objectiveId]);

  /**
   * Copy with the learner's own numbers in it.
   *
   * The same local `fill` the schedule panel uses, and for the same reason: Spanish
   * puts these values in a different order, so composing a sentence out of JSON
   * fragments in the component would leave one language reading correctly and the
   * other not.
   */
  function fill(key: CopyKey, values: Record<string, string>): string {
    return t(key).replace(/\{(\w+)\}/g, (_, name: string) => values[name] ?? '');
  }

  const subjects = useMemo(() => {
    const rows = [...(progress?.subjects ?? [])];
    if (ordering === 'plan') return rows.sort((a, b) => a.order - b.order);
    /*
     * Weakest first, and unmeasured subjects last rather than treated as zero — they
     * are not the answer to "what am I worst at", they are a gap in the plan.
     *
     * Ties break on weight, which matters more than it looks: on a curriculum nobody
     * has started every subject is at nought, and without the tiebreak the list would
     * be in an arbitrary order on exactly the day it is first looked at. With it, the
     * first row is the heaviest subject not yet begun.
     */
    return rows.sort((a, b) => {
      if (a.mastery === null || b.mastery === null) {
        return a.mastery === b.mastery ? a.order - b.order : a.mastery === null ? 1 : -1;
      }
      if (a.mastery !== b.mastery) return a.mastery - b.mastery;
      if (a.weight !== b.weight) return b.weight - a.weight;
      return a.order - b.order;
    });
  }, [progress, ordering]);

  if (progress === undefined) {
    return (
      <section className="panel">
        <h2>{t('prog.title')}</h2>
        <p className="muted">{t('prog.loading')}</p>
      </section>
    );
  }

  if (!progress) {
    if (unreadable) {
      return (
        <section className="panel">
          <h2>{t('prog.title')}</h2>
          <p className="problem" role="alert">
            {t('prog.unreadable')}
          </p>
        </section>
      );
    }
    /*
     * Nothing running, said out loud, which it did not used to be.
     *
     * This panel returned `null` for that state, and it was right to while it was one
     * panel among a dozen on the objectives screen — the screen around it was already
     * explaining that no objective had been stated. It is its own destination in the
     * menu now, and a menu entry that opens a blank page is indistinguishable from one
     * that failed to load. So the empty state names itself.
     */
    return (
      <section className="panel">
        <h2>{t('prog.title')}</h2>
        <p className="muted">{t('prog.idle')}</p>
      </section>
    );
  }

  const { overall, load, pace } = progress;
  const numbers = {
    questions: String(overall.questions),
    answered: String(overall.answered),
    holding: String(overall.holding),
    struggling: String(overall.struggling),
    untested: String(overall.untested),
    due: String(load.dueNow),
    recall: overall.recall === null ? '' : percent(overall.recall),
  };

  return (
    <section className="panel">
      <h2>{t('prog.title')}</h2>

      {progress.mastery === null ? (
        <p className="muted">{t('prog.headline.none')}</p>
      ) : (
        <p className="headline">
          <span className="headline-figure">{percent(progress.mastery)}</span>
          <span className="headline-caption">{t('prog.headline')}</span>
        </p>
      )}

      <Shape
        holding={overall.holding}
        struggling={overall.struggling}
        untested={overall.untested}
      />
      <p className="muted small">{fill('prog.counts', numbers)}</p>

      {/* The denominator is in the sentence, not implied by it. One question answered
          out of four hundred and sixty-nine is the fact that makes the percentage
          beside it readable. */}
      <p className="plain">
        <span>
          {overall.answered === 0
            ? t('prog.answered.none')
            : fill('prog.answered', numbers)}
        </span>
        <span>
          {/*
           * `standard` rather than the target as typed. The two are the same number
           * almost always and differ exactly when somebody asked for 99%, which the
           * scheduler refuses and holds at 97 — quoting their own figure back at them
           * would be quoting one nothing honours.
           */}
          {pace.mode === 'score'
            ? fill('prog.pace.score', { target: percent(progress.standard) })
            : fill(pace.feasible ? 'prog.pace.date.ok' : 'prog.pace.date.tight', {
                days: String(pace.daysLeft),
                // Rounded **up**. Rounding a required daily load down is the direction
                // that makes an impossible plan look survivable, which is the one
                // error this figure exists to prevent.
                required: String(Math.ceil(pace.requiredPerDay)),
                capacity: String(pace.capacityPerDay),
              })}
        </span>
      </p>

      {/*
        * The printable record, sitting under the sentence that says how much of it
        * there is. It is a statement about the same set of questions — the ones that
        * have actually been answered — so it belongs beside that number rather than
        * at the bottom of a panel about pace and subjects.
        */}
      <h3 className="label">{t('sheet.title')}</h3>
      {overall.answered === 0 ? (
        // A button whose only possible outcome is a refusal is not an offer. Nothing
        // answered yet is an ordinary state, so it gets the sentence and not an error.
        <p className="muted small">{t('sheet.error.empty')}</p>
      ) : (
        <>
          <p className="muted small">{t('sheet.hint')}</p>
          <div className="actions">
            <button
              type="button"
              className="button"
              disabled={downloading}
              onClick={() => {
                setDownloading(true);
                setSheetError(null);
                // This tab's plan, not the default one: the route has taken a plan id since
                // step 6 and a sheet is a PDF of somebody's own topics with nothing on it to
                // say which plan it came out of.
                void downloadSheet(objectiveId).then((result) => {
                  setDownloading(false);
                  if (!isError(result)) return;
                  /*
                   * Three outcomes, three sentences. "Nothing to put on a sheet" and
                   * "the rewrite did not come back this time" are opposite messages —
                   * one is a fact about the learner's record and the other is a fault
                   * here that a second press usually clears.
                   */
                  setSheetError(
                    result.error === 'nothing_learned'
                      ? 'sheet.error.empty'
                      : result.error === 'rewrite_failed'
                        ? 'sheet.error.rewrite'
                        : 'sheet.error.generic',
                  );
                });
              }}
            >
              {downloading ? t('sheet.working') : t('sheet.download')}
            </button>
          </div>
          {sheetError && (
            <p className="problem" role="alert">
              {t(sheetError)}
            </p>
          )}
        </>
      )}

      <h3 className="label">{t('prog.load.title')}</h3>
      <p className="plain">
        <span>
          {load.dailyLimit === null
            ? t('prog.load.noLimit')
            : fill('prog.load', {
                limit: String(load.dailyLimit),
                sent: load.sentToday === null ? '—' : String(load.sentToday),
                answered: String(load.answeredToday),
                due: String(load.dueNow),
              })}
        </span>
        {/* Said every time, not only when the numbers look odd. A cap read as a rate
            is how somebody concludes the system is broken because it sent two things
            on a day it was allowed ten. */}
        {load.dailyLimit !== null && (
          <span>{fill('prog.load.ceiling', { limit: String(load.dailyLimit) })}</span>
        )}
        {load.paused && (
          <span>
            {load.pausedReason === 'no_answer'
              ? t('prog.load.pausedBySilence')
              : t('prog.load.paused')}
          </span>
        )}
        {!load.paused && load.awaitingAnswer && <span>{t('prog.load.waiting')}</span>}
        {load.timezone === null && <span>{t('prog.load.noZone')}</span>}
      </p>

      <h3 className="label">{t('prog.subjects.title')}</h3>
      <div className="actions">
        <button
          type="button"
          className="linky"
          onClick={() => setOrdering(ordering === 'weakest' ? 'plan' : 'weakest')}
        >
          {t(ordering === 'weakest' ? 'prog.subjects.byPlan' : 'prog.subjects.byWeakest')}
        </button>
      </div>

      <ol className="subjects">
        {subjects.map((subject) => (
          <Subject
            key={subject.nodeId}
            subject={subject}
            label={fill('prog.subject.counts', {
              holding: String(subject.evidence.holding),
              struggling: String(subject.evidence.struggling),
              untested: String(subject.evidence.untested),
              questions: String(subject.evidence.questions),
            })}
          />
        ))}
      </ol>

      {/*
       * Everything the numbers above leave out, said rather than hidden.
       *
       * Each of these is a real reason the counts might not add up the way somebody
       * checking them would expect, and an unexplained discrepancy on a progress
       * screen reads as the whole screen being wrong.
       */}
      <div className="caveats">
        {progress.explanations > 0 && (
          <p className="muted small">
            {fill('prog.note.explanations', { count: String(progress.explanations) })}
          </p>
        )}
        {progress.subjectsWithoutQuestions > 0 && (
          <p className="muted small">
            {fill('prog.note.noQuestions', {
              count: String(progress.subjectsWithoutQuestions),
            })}
          </p>
        )}
        {progress.unplaced > 0 && (
          <p className="muted small">
            {fill('prog.note.unplaced', { count: String(progress.unplaced) })}
          </p>
        )}
        {progress.subjects.some((subject) => !subject.active) && (
          <p className="muted small">{t('prog.note.off')}</p>
        )}
        {progress.partial && (
          <p className="problem" role="status">
            {t('prog.note.partial')}
          </p>
        )}
      </div>
    </section>
  );
}
